export default class WebGLBackground {
  constructor(el) {
    this.$el = $(el);
    this.canvas = null;
    this.gl = null;
    this.prog = null;
    this.locR = null;
    this.locT = null;
    this.t0 = null;
    this.animationFrame = null;
  }

  init() {
    if (!this.$el.length) {
      return;
    }

    // eslint-disable-next-line prefer-destructuring
    this.canvas = this.$el[0];
    this.gl = this.canvas.getContext('webgl', { antialias: false, alpha: false });

    this.setupCanvas();
    this.setupShaders();
    this.setupBuffers();
    this.setupUniforms();
    this.startAnimation();

    window.addEventListener('resize', () => this.handleResize());
  }

  setupCanvas() {
    this.handleResize();
  }

  handleResize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(window.innerWidth * dpr);
    this.canvas.height = Math.floor(window.innerHeight * dpr);
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  setupShaders() {
    const vert = `
      attribute vec2 p;
      void main(){ gl_Position = vec4(p,0.0,1.0); }
      `;

    const frag = `
      precision highp float;
      uniform vec2 r;
      uniform float t;

      float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
      float noise(vec2 p){
      vec2 i=floor(p), f=fract(p);
      float a=hash(i), b=hash(i+vec2(1,0)), c=hash(i+vec2(0,1)), d=hash(i+vec2(1,1));
      vec2 u=f*f*(3.0-2.0*f);
      return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
      }
      float fbm(vec2 p){
      float v=0.0, a=0.5;
      mat2 m = mat2(1.6,1.2,-1.2,1.6);
      for(int i=0;i<4;i++){
      v += a*noise(p);
      p = m*p;
      a *= 0.5;
      }
      return v;
      }
      float ease(float x){ return x*x*(3.0-2.0*x); }

      vec3 pastelPalette(float x, float verticalBias, float horizontalBias){
        vec3 c1 = vec3(0.059, 0.863, 0.745); // #0FDCBE
        vec3 c2 = vec3(0.498, 0.890, 0.835); // #7fe3d5
        vec3 c3 = vec3(0.647, 0.828, 0.941); // #a5d3f0
        vec3 c4 = vec3(0.043, 0.310, 0.525); // #0b4f86
        vec3 c5 = vec3(0.0, 0.0, 0.0);       // #000000

      x = fract(x);
      float s = x * 4.0;
      float i = floor(s);
      float f = ease(fract(s));

      vec3 a = (i < 1.0) ? c1 : (i < 2.0) ? c2 : (i < 3.0) ? c3 : c4;
      vec3 b = (i < 1.0) ? c2 : (i < 2.0) ? c3 : (i < 3.0) ? c4 : c1;

      vec3 baseColor = mix(a,b,f);
      float verticalBlack = smoothstep(0.12, 0.88, verticalBias);
      float edgeTop = 0.2;
      float slant = tan(radians(30.0));
      float edgeBottom = edgeTop - slant;
      float diagonalThreshold = mix(edgeTop, edgeBottom, verticalBias);
      float horizontalBand = 0.32;
      float horizontalBlack = smoothstep(diagonalThreshold - 0.06, diagonalThreshold + horizontalBand, horizontalBias);
      float blend = verticalBlack * horizontalBlack;
      blend = blend * blend * (3.0 - 2.0 * blend);
      float blackMix = blend * 0.8;
      return mix(baseColor, c5, blackMix);
      }

      void main(){
      vec2 uv = (gl_FragCoord.xy - 0.5*r) / r.y;

      float verticalPos = 1.0 - gl_FragCoord.y / r.y;
      float horizontalPos = gl_FragCoord.x / r.x;

      float startOffset = 0.0;
      float tt = -(t + startOffset);

      vec2 dir = normalize(vec2(-0.3, -1.0));
      uv += dir * (tt * 0.06);

      float w1 = fbm(uv*1.4 + tt*0.04);
      float w2 = fbm(uv*2.3 - tt*0.055);
      uv += 0.35 * (vec2(w1,w2) - 0.5);

      float sines =
      sin(2.4*uv.x + tt*0.45) +
      sin(2.4*uv.y - tt*0.4) +
      sin(2.4*(uv.x+uv.y) + tt*0.35);

      float n = fbm(uv*3.0 + tt*0.08);

      float v = 0.55 + 0.17*sines + 0.38*(n - 0.5);
      v += 0.10 * sin(tt*0.18 + n*6.28318);
      v -= verticalPos * 0.3;

      float g = noise(gl_FragCoord.xy*0.8 + tt*60.0);
      v += (g - 0.5) * 0.035;

      vec3 col = pastelPalette(v, verticalPos, horizontalPos);

      float vign = smoothstep(1.25, 0.25, length((gl_FragCoord.xy - 0.5*r)/r.y));
      col *= 0.92 + 0.25*vign;

      gl_FragColor = vec4(col, 1.0);
      }
  `;

    this.prog = this.gl.createProgram();
    this.gl.attachShader(this.prog, this.compileShader(this.gl.VERTEX_SHADER, vert));
    this.gl.attachShader(this.prog, this.compileShader(this.gl.FRAGMENT_SHADER, frag));
    this.gl.linkProgram(this.prog);

    if (!this.gl.getProgramParameter(this.prog, this.gl.LINK_STATUS)) {
      throw new Error(this.gl.getProgramInfoLog(this.prog) || 'Program link failed');
    }

    this.gl.useProgram(this.prog);
  }

  compileShader(type, src) {
    const shader = this.gl.createShader(type);
    this.gl.shaderSource(shader, src);
    this.gl.compileShader(shader);

    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      throw new Error(this.gl.getShaderInfoLog(shader) || 'Shader compile failed');
    }

    return shader;
  }

  setupBuffers() {
    const buf = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buf);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), this.gl.STATIC_DRAW);

    const locP = this.gl.getAttribLocation(this.prog, 'p');
    this.gl.enableVertexAttribArray(locP);
    this.gl.vertexAttribPointer(locP, 2, this.gl.FLOAT, false, 0, 0);
  }

  setupUniforms() {
    this.locR = this.gl.getUniformLocation(this.prog, 'r');
    this.locT = this.gl.getUniformLocation(this.prog, 't');
  }

  startAnimation() {
    this.t0 = performance.now();
    this.animate();
  }

  animate() {
    const time = (performance.now() - this.t0) / 1000;
    this.gl.uniform2f(this.locR, this.canvas.width, this.canvas.height);
    this.gl.uniform1f(this.locT, time);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    window.removeEventListener('resize', this.handleResize);
  }
}
if (typeof window.acf === 'undefined') {
  $(document).ready(() => {
    $('.mod-brand-statement-banner__floating-canvas').each((_index, element) => {
      new WebGLBackground(element).init();
    });
  });
}
